import React, { useState, useMemo, useContext, useEffect } from 'react';
import { Modal, Button, Radio, Alert, Spin } from 'antd';
import {
  SyncOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { ISubscriptionItem } from '../../../../pages/subscriptions/controllers/useSubscriptionsPageController';
import { getNumberPlanBadgeClass, formatPlanLeedbucks, isUnlimitedLeedbucks } from '../../../../helpers';
import { useLeedbucksConfig } from '../../../../hooks/plans/useLeedbucksConfig';
import CompanyContext from '../../../../contexts/company/CompanyContext';
import PlansContext from '../../../../contexts/plans/PlansContext';
import dayjs from 'dayjs';
import { billingIntervalAsString, billingIntervalAsSuffix, EBillingInterval } from '@leedpulse-monorepo/enums';
import { IPlan } from '@leedpulse-monorepo/interfaces';
import { ECreditType } from '@leedpulse-monorepo/enums';
import './ChangeSubscriptionModal.css';

enum EPlanChangeType {
  UPGRADE = 'upgrade',
  DOWNGRADE = 'downgrade',
  CURRENT = 'current',
}

/** One selectable row in the modal (duplicates in upgradableTo/downgradableTo become separate rows). */
interface PlanDisplaySlot {
  slotKey: string;
  planId: string;
  changeType: EPlanChangeType;
  plan: IPlan | null;
}

interface ChangeSubscriptionModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  subscription: ISubscriptionItem | null;
  onCancel: () => void;
  onConfirm: (subscription: ISubscriptionItem, newPlanId: string) => void;
}

/**
 * Build ordered display list: upgrades (upgradableTo order, duplicates kept) → current → downgrades (downgradableTo order).
 */
const buildPlanDisplaySlots = (
  currentPlan: IPlan,
  planCache: Map<string, IPlan>
): PlanDisplaySlot[] => {
  const slots: PlanDisplaySlot[] = [];

  // A point of sale bills on one rhythm: a plan on the other one is not reachable from here,
  // whatever the catalogue lists. The backend refuses it too.
  const currentInterval = currentPlan.price?.interval ?? EBillingInterval.MONTH;
  const isSameInterval = (plan: IPlan | null) => (plan?.price?.interval ?? EBillingInterval.MONTH) === currentInterval;

  
    (currentPlan.downgradable_to ?? []).forEach((planId, index) => {
      const plan = planCache.get(planId) ?? null;
      if (plan?.archived) return; // Archiving a plan does not remove it from up/downgrade arrays
      if (!isSameInterval(plan)) return;
      slots.push({
        slotKey: `down-${index}`,
        planId,
        changeType: EPlanChangeType.DOWNGRADE,
        plan,
      });
    });

  slots.push({
    slotKey: 'current',
    planId: currentPlan.id,
    changeType: EPlanChangeType.CURRENT,
    plan: currentPlan,
  });

  (currentPlan.upgradable_to ?? []).forEach((planId, index) => {
    const plan = planCache.get(planId) ?? null;
    if (plan?.archived) return;
    if (!isSameInterval(plan)) return;
    slots.push({
      slotKey: `up-${index}`,
      planId,
      changeType: EPlanChangeType.UPGRADE,
      plan,
    });
  });

  return slots;
};

const ChangeSubscriptionModal: React.FC<ChangeSubscriptionModalProps> = ({
  isOpen,
  isLoading = false,
  subscription,
  onCancel,
  onConfirm,
}) => {
  const { planCache, fetchPlans, fetchPlanById } = useContext(PlansContext);
  const { activeCompany } = useContext(CompanyContext);
  const { callRate, smsRate } = useLeedbucksConfig();
  const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null);

  const currentPlan = useMemo(() => {
    if (!subscription?.id_plan) return null;
    return planCache.get(subscription.id_plan) ?? null;
  }, [planCache, subscription?.id_plan]);

  const displaySlots = useMemo((): PlanDisplaySlot[] => {
    if (!currentPlan) return [];
    return buildPlanDisplaySlots(currentPlan, planCache);
  }, [currentPlan, planCache]);

  const visibleSlots = useMemo(() => {
    return displaySlots.filter(
      (s) => s.plan !== null
    ) as Array<PlanDisplaySlot & { plan: IPlan }>;
  }, [displaySlots]);

  const selectedSlot = useMemo(() => {
    if (!selectedSlotKey) return null;
    return visibleSlots.find((s) => s.slotKey === selectedSlotKey) ?? null;
  }, [visibleSlots, selectedSlotKey]);

  const selectedPlan = selectedSlot?.plan ?? null;

  const isUpgrade = selectedSlot?.changeType === EPlanChangeType.UPGRADE;
  const isDowngrade = selectedSlot?.changeType === EPlanChangeType.DOWNGRADE;

  const hasSelectedDifferentPlan =
    selectedSlot !== null && selectedSlot.changeType !== EPlanChangeType.CURRENT;

  // Shown before the click. The backend refuses it too - this is not the rule itself.
  const NOTICE_DAYS = 30;
  const periodEndMs = activeCompany?.billing?.period_end ?? null;
  const daysToPeriodEnd = periodEndMs ? dayjs(periodEndMs).diff(dayjs(), 'day') : null;
  const isAnnual = activeCompany?.billing?.interval === EBillingInterval.YEAR;
  const isInsideNotice = isAnnual && daysToPeriodEnd !== null && daysToPeriodEnd < NOTICE_DAYS;

  const priceDifference = useMemo(() => {
    if (!currentPlan || !selectedPlan) return 0;
    return (selectedPlan.price?.value ?? 0) - (currentPlan.price?.value ?? 0);
  }, [currentPlan, selectedPlan]);

  // Plan already scheduled for the next billing date: nothing to request again.
  const pendingPlanChange = subscription?.pending_plan_change ?? null;
  const pendingPlanId = pendingPlanChange?.id_plan ?? null;

  const handlePlanChange = (slotKey: string) => {
    const row = visibleSlots.find((s) => s.slotKey === slotKey);
    if (!row || row.changeType === EPlanChangeType.CURRENT || row.planId === pendingPlanId) return;
    setSelectedSlotKey(slotKey);
  };

  const handleConfirm = () => {
    if (subscription && selectedSlot && selectedSlot.changeType !== EPlanChangeType.CURRENT) {
      onConfirm(subscription, selectedSlot.planId);
    }
  };

  const handleCancel = () => {
    setSelectedSlotKey(null);
    onCancel();
  };

  // Downsell date is the Stripe period end, like the cancel modal
  const getDowngradeDate = (): string => {
    const iPeriodEnd = periodEndMs
      ?? (subscription?.plan_end ? dayjs(subscription.plan_end).subtract(1, 'month').valueOf() : null);

    return iPeriodEnd ? dayjs(iPeriodEnd).locale('fr').format('D MMM YYYY') : '-';
  };

  const formatPrice = (price: number): string => {
    return price.toLocaleString('fr-FR', {
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
  };

  // fetch active Leedflow plans
  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure current plan document is loaded (includes upgradableTo / downgradableTo)
  useEffect(() => {
    if (!isOpen || !subscription?.id_plan) return;
    if (!planCache.has(subscription.id_plan)) {
      fetchPlanById(subscription.id_plan);
    }
  }, [isOpen, subscription?.id_plan, planCache, fetchPlanById]);

  // Preload target plans referenced by the graph (unique ids only for network)
  useEffect(() => {
    if (!isOpen || !currentPlan) return;
    const targetIds = [...new Set([...(currentPlan.upgradable_to ?? []), ...(currentPlan.downgradable_to ?? [])])];
    targetIds.forEach((id) => {
      if (!planCache.has(id)) void fetchPlanById(id);
    });
  }, [isOpen, currentPlan, planCache, fetchPlanById]);

  useEffect(() => {
    if (!isOpen) setSelectedSlotKey(null);
  }, [isOpen]);

  if (!subscription) return null;

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      centered
      width={560}
      className={`change-subscription-modal ${isDowngrade ? 'change-subscription-modal--downgrade' : ''
        }`}
      closable={true}
    >
      <div className="change-subscription-modal__content">
        {/* Header with icon and title */}
        <div className="change-subscription-modal__header">
          <div className="change-subscription-modal__icon">
            <SyncOutlined />
          </div>
          <h2 className="change-subscription-modal__title">Changer d&apos;abonnement</h2>
        </div>

        {/* Number info section */}
        <div className="change-subscription-modal__info">
          <div className="change-subscription-modal__info-row">
            <span className="change-subscription-modal__info-label">Numéro</span>
            <span className="change-subscription-modal__info-label">Plan actuel</span>
          </div>
          <div className="change-subscription-modal__info-row">
            <span className="change-subscription-modal__phone">{subscription.sender_formatted}</span>
            {currentPlan ? (
              <span
                className={`change-subscription-modal__plan-badge ${getNumberPlanBadgeClass(
                  currentPlan.id,
                  'change-subscription-modal'
                )}`}
              >
                {currentPlan.name ?? currentPlan.id}
              </span>
            ) : (
              <span className="change-subscription-modal__plan-badge change-subscription-modal__plan-badge--none">
                -
              </span>
            )}
          </div>
        </div>

        {/* A change is already scheduled: confirming here replaces it */}
        {pendingPlanChange && (
          <Alert
            type="info"
            showIcon
            message={`Un passage au plan ${planCache.get(pendingPlanChange.id_plan)?.name ?? pendingPlanChange.id_plan} est déjà prévu le ${new Date(pendingPlanChange.effective_at).toLocaleDateString('fr-FR')}. Un nouveau changement le remplacera.`}
          />
        )}

        {/* Upgrade/Downgrade info banner */}
        {hasSelectedDifferentPlan && (
          <div
            className={`change-subscription-modal__banner ${isUpgrade
              ? 'change-subscription-modal__banner--upgrade'
              : 'change-subscription-modal__banner--downgrade'
              }`}
          >
            <div className="change-subscription-modal__banner-header">
              {isUpgrade ? (
                <ThunderboltOutlined className="change-subscription-modal__banner-icon" />
              ) : (
                <WarningOutlined className="change-subscription-modal__banner-icon" />
              )}
              <span className="change-subscription-modal__banner-title">
                {isUpgrade ? 'Amélioration immédiate' : 'Changement différé'}
              </span>
            </div>
            <p className="change-subscription-modal__banner-text">
              {isUpgrade
                ? 'Le changement est effectif immédiatement. Les crédits supplémentaires sont ajoutés au prorata. La différence sera calculée au prorata sur votre prochaine facture.'
                : `Le changement prendra effet le ${getDowngradeDate()}, à votre prochaine facturation, sans aucun prorata. D'ici là, votre plan, vos crédits et votre tarif restent inchangés. Vous pourrez annuler ce changement jusqu'à cette date.`}
            </p>
          </div>
        )}

        {/* Plan selection */}
        <div className="change-subscription-modal__plans-section">
          <label className="change-subscription-modal__label">Nouveau plan</label>
          <Radio.Group
            value={selectedSlotKey}
            onChange={(e) => handlePlanChange(e.target.value)}
            className="change-subscription-modal__plans-list"
          >
            {!currentPlan && subscription.id_plan ? (
              <div className="change-subscription-modal__plans-loading">
                <Spin />
              </div>
            ) : visibleSlots.length > 0 ? (
              visibleSlots.map((row) => {
                const { plan, slotKey, changeType } = row;
                const isSelected = selectedSlotKey === slotKey;
                const isCurrent = changeType === EPlanChangeType.CURRENT;
                const isPending = row.planId === pendingPlanId;

                return (
                  <div
                    key={slotKey}
                    className={`change-subscription-modal__plan-card ${isSelected ? 'change-subscription-modal__plan-card--selected' : ''
                      } ${isCurrent || isPending ? 'change-subscription-modal__plan-card--current' : ''}`}
                    onClick={() => handlePlanChange(slotKey)}
                  >
                    <div className="change-subscription-modal__plan-left">
                      <Radio value={slotKey} disabled={isCurrent || isPending} />
                      <div className="change-subscription-modal__plan-info">
                        <div className="change-subscription-modal__plan-name-row">
                          <span className="change-subscription-modal__plan-name">
                            {plan.name ?? plan.id}
                          </span>
                          {isPending && pendingPlanChange && (
                            <span className="change-subscription-modal__change-badge change-subscription-modal__change-badge--current">
                              Prévu le {new Date(pendingPlanChange.effective_at).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                          {!isCurrent && !isPending && (
                            <span
                              className={`change-subscription-modal__change-badge change-subscription-modal__change-badge--${changeType}`}
                            >
                              {changeType === EPlanChangeType.UPGRADE ? (
                                <>
                                  <ArrowUpOutlined /> Supérieur
                                </>
                              ) : (
                                <>
                                  <ArrowDownOutlined /> Inférieur
                                </>
                              )}
                            </span>
                          )}
                          {isCurrent && (
                            <span className="change-subscription-modal__change-badge change-subscription-modal__change-badge--current">
                              Actuel
                            </span>
                          )}
                        </div>
                        <span className="change-subscription-modal__plan-credits">
                          {isUnlimitedLeedbucks(plan.credits?.[ECreditType.leedbucks] ?? 0)
                            ? 'Crédits illimités'
                            : `${(plan.credits?.[ECreditType.leedbucks] ?? 0).toLocaleString('fr-FR')} crédits · ${formatPlanLeedbucks(plan, callRate, smsRate)}`}
                        </span>
                      </div>
                    </div>
                    <div className="change-subscription-modal__plan-price">
                      <span className="change-subscription-modal__plan-price-value">
                        {plan.price?.formatted || '0€'}
                      </span>
                      <span className="change-subscription-modal__plan-price-period">{billingIntervalAsSuffix(plan.price?.interval ?? EBillingInterval.MONTH)} HT</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <Alert
                message="Aucun plan disponible pour le moment, pour tout changement de plan, contactez-nous."
                type="info"
              />
            )}
          </Radio.Group>
        </div>

        {/* Summary Section */}
        {hasSelectedDifferentPlan && currentPlan && selectedPlan && (
          <div className="change-subscription-modal__summary">
            <div className="change-subscription-modal__summary-row">
              <span>Plan actuel</span>
              <span>{currentPlan.price?.formatted || '0€'}{billingIntervalAsSuffix(currentPlan.price?.interval ?? EBillingInterval.MONTH)} HT</span>
            </div>
            <div className="change-subscription-modal__summary-row">
              <span>Nouveau plan</span>
              <span>{selectedPlan.price?.formatted || '0€'}{billingIntervalAsSuffix(selectedPlan.price?.interval ?? EBillingInterval.MONTH)} HT</span>
            </div>
            <div
              className={`change-subscription-modal__summary-row change-subscription-modal__summary-row--highlight ${isUpgrade
                ? 'change-subscription-modal__summary-row--upgrade'
                : 'change-subscription-modal__summary-row--downgrade'
                }`}
            >
              <div className="change-subscription-modal__summary-diff">
                <span>Différence {billingIntervalAsString(selectedPlan.price?.interval ?? EBillingInterval.MONTH).toLowerCase()}</span>
                <span className="change-subscription-modal__summary-note">
                  {isUpgrade
                    ? `Un prorata de ~${formatPrice(priceDifference)}€ sera ajouté à votre prochaine facture`
                    : `Économie effective à partir du ${getDowngradeDate()}`}
                </span>
              </div>
              <span className="change-subscription-modal__summary-diff-value">
                {priceDifference > 0 ? '+' : ''}
                {formatPrice(priceDifference)}€{billingIntervalAsSuffix(selectedPlan.price?.interval ?? EBillingInterval.MONTH)}
              </span>
            </div>
          </div>
        )}

        {isDowngrade && isInsideNotice && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message={`Trop tard pour cette échéance`}
            description={`Un changement à la baisse se demande au moins ${NOTICE_DAYS} jours avant l'échéance, et la vôtre tombe le ${dayjs(periodEndMs).locale('fr').format('D MMM YYYY')}. Contactez le support pour cette échéance-ci.`}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Actions */}
        <div className="change-subscription-modal__actions">
          <Button className="change-subscription-modal__cancel-btn" onClick={handleCancel}>
            Annuler
          </Button>
          <Button
            type="primary"
            className={`change-subscription-modal__confirm-btn ${isDowngrade
              ? 'change-subscription-modal__confirm-btn--downgrade'
              : 'change-subscription-modal__confirm-btn--upgrade'
              }`}
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!hasSelectedDifferentPlan || (isDowngrade && isInsideNotice)}
          >
            {isDowngrade ? (
              <>
                <ArrowDownOutlined /> Réduire le plan
              </>
            ) : (
              <>
                <ArrowUpOutlined /> Améliorer le plan
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ChangeSubscriptionModal;
